import { cloneDeep } from 'lodash';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  isSameHour,
  startOfHour,
  startOfDay,
  startOfMonth,
  subDays,
  isSameMinute,
} from 'date-fns';
import { fi } from 'date-fns/locale';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../components/Graphs/ReportingGraph/styles/ReportingGraph.module.scss';

// Utils.
import { generateAddress } from './address';
import { roundNumber, setPriceFormat } from './billing';
// Types.
import { TFunction } from 'i18next';
import { ConsumptionItem, GraphTooltipType, MeteringPointOption } from '../types/consumptionRates';
import { ContractBasicData, Weather, WeatherDataV2 } from '../types/contract';
import { EliqForecast } from '../types/eliq';
import { NordPoolSpotGraphDatapoint, ReportingGraphDatapoint } from '../types/graphs';
const pastEstimateDays = 2;
import { Ledger } from '../types/billing';

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getDateFormat(resolution: string, nordpool = false): string {
  let format = '';

  if (resolution === 'year') {
    format = 'yyyy';
  }
  if (resolution === 'month') {
    if (nordpool) {
      format = 'M/yyyy';
    } else {
      format = 'LLLL yyyy';
    }
  }
  if (resolution === 'week') {
    format = 'w/yyyy';
  }
  if (resolution === 'day') {
    if (nordpool) {
      format = 'dd.MM.yyyy';
    } else {
      format = 'eeeeee dd.MM.yyyy';
    }
  }
  if (resolution === 'hour') {
    format = 'HH';
  }

  return format;
}

export const graphDateStyle = (startDate: Date | undefined, endDate: Date | undefined, activeFilter: string) => {
  if (
    startDate &&
    endDate &&
    ((activeFilter === 'month' && !isSameDay(startOfMonth(startDate), startOfMonth(endDate))) ||
      (activeFilter === 'month' && !(isSameDay(startDate, startOfMonth(startDate)) && isSameDay(endDate, endOfMonth(startDate)))) ||
      (activeFilter === 'day' && !isSameDay(startDate, endDate)))
  ) {
    return 'custom';
  }
  return activeFilter;
};

/**
 * Merge consumption data and temperature data to one simplified array
 *
 * @param consumptionRates ConsumptionRates[]
 * @param activeFilter string
 * @param t TFunction
 * @param temperatures Temperature
 */
export function generateDataPoints(
  consumptionRates: ConsumptionItem[],
  activeFilter: string,
  t: TFunction,
  customerType: string,
  contractIsNoPrice: boolean,
  meteringPointType: string,
  consumptionForecast?: EliqForecast,
  weather?: WeatherDataV2,
  spotData?: NordPoolSpotGraphDatapoint[],
): ReportingGraphDatapoint[] {
  const dataPoints: ReportingGraphDatapoint[] = [];

  consumptionRates.forEach((dataPoint, index) => {
    const point: ReportingGraphDatapoint = {};
    point.time = dataPoint.startTime;
    point.consumption = dataPoint.sum;
    point.consumptionAreaChart = dataPoint.sum;
    point.costWithVat = dataPoint.costWithVat;
    point.costWithoutVat = dataPoint.costWithoutVat;
    point.customerType = customerType;
    point.contractIsNoPrice = contractIsNoPrice;
    point.meteringPointType = meteringPointType;
    point.resolution = activeFilter;

    let pointTemperature: Weather | undefined = undefined;
    let pointSpotPrice: number | undefined = undefined;

    if (activeFilter !== 'day') {
      let offset = pastEstimateDays + 1;

      // merge first bar of estimates
      if (
        consumptionForecast?.forecast &&
        startOfDay(new Date(consumptionForecast.from)) <= startOfDay(new Date()) &&
        (((activeFilter === 'month' || activeFilter === 'week') &&
          new Date(dataPoint.startTime) >= startOfDay(new Date(consumptionForecast.from.split('T')[0])) &&
          index === consumptionRates.length - offset &&
          consumptionForecast.forecast[0] !== null) ||
          (activeFilter === 'year' && index === consumptionRates.length - 1))
      ) {
        const estimate = consumptionForecast.forecast[0] / 1000 - dataPoint.sum;
        if (estimate >= 0 && !(activeFilter !== 'year' && point.consumption > 0)) {
          point.consumptionEstimate = estimate;
          point.consumptionAreaChart = point.consumption + estimate;
          point.consumptionEstimateAreaChart = point.consumption + estimate;
        }
      }

      const mergeBar = (forecastIndex: number) => {
        if (
          activeFilter === 'week' &&
          isBefore(startOfDay(new Date(consumptionForecast?.to || new Date())), startOfDay(new Date())) &&
          offset > consumptionRates.length
        ) {
          offset = 9;
        }
        if (
          (activeFilter === 'month' || activeFilter === 'week') &&
          consumptionForecast?.forecast &&
          (isSameDay(new Date(consumptionForecast.from), new Date()) || isBefore(new Date(consumptionForecast.from), new Date())) &&
          index === consumptionRates.length - (offset - forecastIndex) &&
          consumptionForecast.forecast[forecastIndex] !== null &&
          consumptionForecast.forecast.length > 1
        ) {
          const estimate = consumptionForecast.forecast[forecastIndex] / 1000 - dataPoint.sum;
          if (!isNaN(estimate) && estimate > 0 && !point.consumption) {
            point.consumptionEstimate = estimate;
            point.consumptionEstimateAreaChart = estimate;
            point.consumptionAreaChart = estimate;
          }
          if (point.consumptionAreaChart && point.consumptionAreaChart > 0 && point.consumption === 0) {
            point.consumption = undefined;
            if (forecastIndex > 1) {
              point.consumptionAreaChart = undefined;
            }
          }
        }
      };

      for (let i = 1; i <= pastEstimateDays; i++) {
        mergeBar(i);
      }
    }

    if (activeFilter === 'year') {
      point.name = capitalizeFirstLetter(format(new Date(dataPoint.startTime), 'LLL', { locale: fi }));
      if (weather && weather.data.length > 0) {
        pointTemperature = weather.data.find((w) => w !== undefined && w !== null && isSameDay(new Date(w.time), new Date(dataPoint.startTime)));
      }
      if (spotData && spotData.length > 0) {
        pointSpotPrice = spotData?.find((spot) => isSameDay(new Date(spot.period.start), new Date(dataPoint.startTime)))?.meanPriceWithVat;
      }
    }
    if (activeFilter === 'month') {
      point.name = format(new Date(dataPoint.startTime), 'd');
    }
    if (activeFilter === 'week') {
      point.name = capitalizeFirstLetter(format(new Date(dataPoint.startTime), 'cccc', { locale: fi }));
    }
    if (activeFilter === 'day') {
      point.name = format(new Date(dataPoint.startTime), 'HH');
    }
    if (activeFilter === 'week' || activeFilter === 'month') {
      if (weather && weather.data.length > 0) {
        pointTemperature = weather.data.find((w) => w !== undefined && w !== null && isSameDay(new Date(w.time), new Date(dataPoint.startTime)));
      }
      if (spotData && spotData.length > 0) {
        pointSpotPrice = spotData?.find((spot) => isSameDay(new Date(spot.period.start), new Date(dataPoint.startTime)))?.meanPriceWithVat;
      }
    }
    if (activeFilter === 'day' && weather) {
      pointTemperature = weather.data.find(
        (w) =>
          w !== undefined &&
          w !== null &&
          isSameHour(new Date(w.time), new Date(dataPoint.startTime)) &&
          isSameMinute(new Date(w.time), new Date(dataPoint.startTime)),
      );
    }
    if (spotData && spotData.length > 0) {
      pointSpotPrice = spotData?.find(
        (spot) =>
          isSameHour(new Date(spot.period.start), new Date(dataPoint.startTime)) &&
          isSameMinute(new Date(spot.period.start), new Date(dataPoint.startTime)),
      )?.meanPriceWithVat;
    }
    if ((activeFilter === 'month' || activeFilter === 'week') && weather && weather.forecast.length > 0) {
      const temperature = weather.forecast.find(
        (f) => f !== undefined && f !== null && isSameHour(new Date(f.timestamp), addHours(new Date(cloneDeep(dataPoint.startTime)), 15)),
      );

      if (temperature) {
        point.temperatureForecast = temperature.temperature.value;
        point.station = weather?.location;
      }
    }
    if (pointTemperature) {
      point.temperature = pointTemperature.temperatureAvg;
      if (isSameDay(new Date(dataPoint.startTime), subDays(new Date(), 1))) {
        point.temperatureForecast = pointTemperature.temperatureAvg;
      }
      point.station = pointTemperature.location;
    }
    if (pointSpotPrice !== undefined) {
      point.spotPrice = pointSpotPrice / 10;
    }

    dataPoints.push(point);
  });
  if (weather && weather.data.length > 0) {
    dataPoints.forEach((time, index) => {
      const pointTime = new Date(time.time || new Date());
      const temperature = weather.data.find(
        (w) => w !== undefined && w !== null && isSameHour(new Date(w.time), pointTime) && isSameMinute(new Date(w.time), pointTime),
      );

      if (temperature) {
        dataPoints[index]['temperature'] = temperature.temperatureAvg;
      }
    });
  }

  const forecastDataPoints: ReportingGraphDatapoint[] = [];

  if (consumptionForecast?.forecast && activeFilter !== 'day') {
    const forecast = consumptionForecast;
    const offset = activeFilter === 'year' ? 1 : pastEstimateDays + 1;
    forecast?.forecast.slice(offset, forecast.forecast.length).forEach((estimate, index) => {
      const point: ReportingGraphDatapoint = {};

      let time = new Date(forecast.from);

      if (forecast.resolution === 'month') {
        time = addMonths(new Date(forecast.from), index + offset);

        point.name = capitalizeFirstLetter(format(time, 'LLL', { locale: fi }));
      }
      if (forecast.resolution === 'day') {
        time = addDays(new Date(forecast.from), index + offset);
      }
      if (activeFilter === 'month') {
        point.name = format(time, 'd');
      }
      if (activeFilter === 'week') {
        point.name = capitalizeFirstLetter(format(time, 'cccc', { locale: fi }));
      }
      if (activeFilter === 'day') {
        point.name = format(time, 'HH');
      }

      point.time = time.toISOString();
      if (estimate >= 0) {
        point.consumptionEstimate = estimate / 1000;
        point.consumptionEstimateAreaChart = estimate / 1000;
      }
      point.resolution = activeFilter;

      const temperature = weather?.forecast.find(
        (f) => f !== undefined && f !== null && isSameHour(new Date(f.timestamp), addHours(new Date(time), 12)),
      );

      if (temperature) {
        point.temperatureForecast = temperature.temperature.value;
        point.station = weather?.location;
      }

      if (point.consumptionEstimate) {
        forecastDataPoints.push(point);
      }
    });
  }
  return dataPoints.concat(forecastDataPoints);
}

export const CustomTooltip: React.FunctionComponent<GraphTooltipType> = ({ active, payload, label }: GraphTooltipType) => {
  const { t } = useTranslation();

  if (active && payload) {
    const currentPayload = payload[0]?.payload;
    let title = label;

    if (currentPayload) {
      if (currentPayload.resolution === 'year') {
        title = capitalizeFirstLetter(format(new Date(currentPayload.time || ''), getDateFormat('month'), { locale: fi }));
      }
      if (currentPayload.resolution === 'month' || currentPayload.resolution === 'week') {
        title = capitalizeFirstLetter(format(new Date(currentPayload.time || ''), getDateFormat('day'), { locale: fi }));
      }
      if (currentPayload.resolution === 'day') {
        title = `${capitalizeFirstLetter(format(new Date(currentPayload.time || ''), getDateFormat('day'), { locale: fi }))} ${t(
          `consumption:consumption.reportingGraph-timeAt`,
        )} ${format(new Date(currentPayload.time || ''), 'HH:mm', { locale: fi })}`;
      }

      const temperature: number | boolean =
        currentPayload.temperature !== undefined && !Number.isNaN(currentPayload.temperature) ? currentPayload.temperature : false;
      const temperatureForecast: number | boolean =
        currentPayload.temperatureForecast && !Number.isNaN(currentPayload.temperatureForecast) ? currentPayload.temperatureForecast : false;
      const spotPrice: number | boolean =
        currentPayload.spotPrice !== undefined && !Number.isNaN(currentPayload.spotPrice) ? currentPayload.spotPrice : false;

      const meteringPointTypeIsProduction = currentPayload.meteringPointType?.toLowerCase() === 'production';

      return (
        <div className="tooltip-lumme">
          <div className="tooltip-title">{title}</div>
          <div>
            <span>{t(`consumption:consumption.reportingGraph-${meteringPointTypeIsProduction ? 'production' : 'consumption'}`)}</span>
            <span>{currentPayload.consumption ? lessThan0Point01(currentPayload.consumption) : 0} kWh</span>
          </div>
          {!currentPayload.contractIsNoPrice && currentPayload.customerType === 'private' && !meteringPointTypeIsProduction && (
            <div>
              <span>{t(`consumption:consumption.reportingGraph-costWithVat`)}</span>
              <span>{currentPayload.costWithVat ? lessThan0Point01(currentPayload.costWithVat) : 0} €</span>
            </div>
          )}
          {!currentPayload.contractIsNoPrice && (currentPayload.customerType === 'business' || meteringPointTypeIsProduction) && (
            <div>
              <span>{t(`consumption:consumption.reportingGraph-costWithoutVat`)}</span>
              <span>{currentPayload.costWithoutVat ? lessThan0Point01(currentPayload.costWithoutVat) : 0} €</span>
            </div>
          )}
          {currentPayload.consumptionEstimate && (
            <div>
              <span>{t(`consumption:consumption.reportingGraph-forecast`)}</span>
              <span>{setPriceFormat(roundNumber(currentPayload.consumptionEstimate))} kWh</span>
            </div>
          )}
          {currentPayload.consumptionEstimate && currentPayload.resolution === 'year' && (
            <div>
              <span>{t(`consumption:consumption.reportingGraph-total`)}</span>
              <span>{setPriceFormat(roundNumber((currentPayload.consumption ?? 0) + currentPayload.consumptionEstimate))} kWh</span>
            </div>
          )}
          {temperature !== false && (
            <>
              <div>
                <span>{t(`consumption:consumption.reportingGraph-temperature`)}</span>
                <span>{roundNumber(temperature, false, 1)} °C</span>
              </div>
              {currentPayload.station && (
                <div>
                  <span>{t(`consumption:consumption.reportingGraph-station`)}</span>
                  <span>{`${currentPayload.station}`}</span>
                </div>
              )}
            </>
          )}
          {!temperature && temperatureForecast && (
            <>
              <div>
                <span>{t(`consumption:consumption.reportingGraph-temperatureForecast`)}</span>
                <span>{roundNumber(temperatureForecast, false)} °C</span>
              </div>
              {currentPayload.station && (
                <div>
                  <span>{t(`consumption:consumption.reportingGraph-place`)}</span>
                  <span>{`${currentPayload.station}`}</span>
                </div>
              )}
            </>
          )}
          {spotPrice !== false && (
            <div>
              <span>{t(`contracts:contracts.spotPrice`)}</span>
              <span>
                {setPriceFormat(roundNumber(spotPrice))} {t('consumption:nordPoolGraph.spotUnit')}
              </span>
            </div>
          )}
          {currentPayload.comparisonConsumption && (
            <>
              <div className="tooltip-title">
                {currentPayload.comparisonName}
                {currentPayload.resolution === 'week' ? `/${new Date(cloneDeep(currentPayload).time || new Date()).getFullYear() - 1}` : ''}
              </div>
              <div>
                <span>{t(`consumption:consumption.reportingGraph-${meteringPointTypeIsProduction ? 'production' : 'consumption'}`)}</span>
                <span>{currentPayload.comparisonConsumption ? setPriceFormat(roundNumber(currentPayload.comparisonConsumption)) : 0} kWh</span>
              </div>
            </>
          )}
        </div>
      );
    }
  }
  return null;
};

export const generateMeteringPointOptions = (
  contracts: ContractBasicData[],
  showAllContracts = false,
  ledgers: Ledger[] = [],
  showOtherContracts = false,
): MeteringPointOption[] => {
  const { t } = useTranslation();
  const hideInvoiceComment = [7, 15, 11, 8, 33, 31, 29];

  const usedMeteringPoints: string[] = [];

  const options: MeteringPointOption[] = [];

  if (showAllContracts && (contracts.length > 1 || (ledgers && ledgers.length > 1))) {
    options.push({
      value: 'all',
      label: t(`billing:billing.showAll`),
      type: 'all',
    });
  }

  if (contracts && contracts.length > 0) {
    contracts.forEach((contract) => {
      const { contractNo, contractCode, GSRN, meteringpointAddress, contractTypeNo, meteringpointType, contractInvoiceComment } = contract;

      if ((GSRN && !usedMeteringPoints.includes(GSRN)) || showAllContracts || showOtherContracts) {
        const streetAddress = hideInvoiceComment.includes(parseInt(contractTypeNo))
          ? generateAddress(meteringpointAddress, false)
          : generateAddress(meteringpointAddress, false, contractInvoiceComment ?? '');

        usedMeteringPoints.push(GSRN);

        options.push({
          value: `${GSRN}-${contractNo}-${contractCode}`,
          label: streetAddress.length > 0 ? streetAddress : `${t('common:general.contract')} ${contractCode}`,
          type: meteringpointType ? meteringpointType.toLowerCase() : 'other',
        });
      }
    });
  }

  return options;
};

export const ReferenceLabel = (props: { value: string; viewBox?: { x: number; y: number } }) => {
  const { value, viewBox } = props;
  const x = viewBox ? viewBox.x + 8 : 0;
  const y = viewBox ? viewBox.y - 14 : 0;
  return (
    <foreignObject x={x} y={y} width={200} height={23}>
      <div className={styles.avg}>{value}</div>
    </foreignObject>
  );
};

export const getMonthList = (locale = 'fi', format: 'long' | 'short' = 'short'): string[] => {
  const year = new Date().getFullYear();
  const monthList = [...Array(12).keys()];
  const formatter = new Intl.DateTimeFormat(locale, {
    month: format,
  });

  const getMonthName = (monthIndex: number) => formatter.format(new Date(year, monthIndex));

  return monthList.map(getMonthName);
};

export const getCurrent15min = () => {
  const minutes = new Date().getMinutes();
  let current15min = startOfHour(new Date());

  if (minutes >= 15 && minutes < 30) {
    current15min = addMinutes(startOfHour(new Date()), 15);
  } else if (minutes >= 30 && minutes < 45) {
    current15min = addMinutes(startOfHour(new Date()), 30);
  } else if (minutes >= 45) {
    current15min = addMinutes(startOfHour(new Date()), 45);
  }
  return current15min;
};

export const lessThan0Point01 = (value: number): string => {
  if (value >= 0.01) {
    return setPriceFormat(roundNumber(value));
  }
  return '< 0,01';
};
